import { useLocale, useTranslation } from '@dreamstack/i18n'
import { SimpleTextLink } from '@dreamstack/simple-components/src/lib/SimpleTextLinks'
import { cn } from '@dreamstack/util/src/cn'
import Link from 'next/link'
import React, { type FunctionComponent } from 'react'

const Container: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn(
      'font-normal text-px-16',
      'ml:text-px-20 ml:leading-px-30',
      className
    )}
    {...rest}
  />
)

export const StyledLegalLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<typeof SimpleTextLink> & { $isOnDarkBg?: boolean }
>(({ $isOnDarkBg, className, ...rest }, ref) => (
  <SimpleTextLink
    ref={ref}
    className={cn(
      'ml:text-px-20 ml:leading-px-30',
      $isOnDarkBg
        ? 'text-accentroAquaMidLight-full font-semibold'
        : 'text-accentroAqua-full',
      className
    )}
    {...rest}
  />
))

StyledLegalLink.displayName = 'StyledLegalLink'

export const LegalImprintText: FunctionComponent<
  React.PropsWithChildren<{
    isOnDarkBg?: boolean
    legalFirm?: string
  }>
> = ({ isOnDarkBg = false, legalFirm = 'ACCENTRO GmbH' }) => {
  const t = useTranslation()
  const locale = useLocale()

  const privacyBaseUrls = {
    de: '/datenschutz',
    en: '/privacy-statement',
  }
  const privacyBaseUrl = privacyBaseUrls[locale]

  const [
    textBeforeLinkBeforeFirm,
    textBeforeLinkAfterFirm,
    textLink,
    textAfterLink,
  ] = t('accentro:legalImprintText', {
    returnObjects: true,
  }) as unknown as string[]
  return (
    <Container>
      {textBeforeLinkBeforeFirm}
      {legalFirm}
      {textBeforeLinkAfterFirm}
      <Link href={privacyBaseUrl} passHref legacyBehavior>
        <StyledLegalLink target="_blank" $isOnDarkBg={isOnDarkBg}>
          {textLink}
        </StyledLegalLink>
      </Link>
      {textAfterLink}
    </Container>
  )
}
